import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPut, axiosPost } from '../api/axios';
import { toast } from 'react-toastify';

export interface PipelineStep {
  id: number;
  step_name: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  error_traceback: string | null;
}

export interface FailedPipelineRun {
  id: number;
  pipeline_name: string;
  entity_id: number;
  status: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  reviewed: boolean;
  steps: PipelineStep[];
  listener_audio_id: number;
  listener_audio_title: string | null;
  listener_audio_ai_title: string | null;
  listener_audio_excerpt: string | null;
  listener_audio_ai_summary: string | null;
  listener_audio_transcription: string | null;
  listener_role_id: number | null;
  listener_name: string | null;
  topic_tag_ids: number[];
}

export interface FailedPipelineRunsResponse {
  count: number;
  data: FailedPipelineRun[];
}

interface FailedPipelineRunsParams {
  page?: number;
  limit?: number;
  include_reviewed?: boolean;
}

const getFailedPipelineRuns = async (params: FailedPipelineRunsParams): Promise<FailedPipelineRunsResponse> => {
  const response = await axiosGet(
    '/pipeline_runs/failed',
    {
      page: params.page || 1,
      limit: params.limit || 50,
      include_reviewed: params.include_reviewed ? 'true' : 'false',
    },
    'v3',
  );
  return response.data;
};

export const useFailedPipelineRuns = (params: FailedPipelineRunsParams) => {
  return useQuery<FailedPipelineRunsResponse>(['failed_pipeline_runs', params], () => getFailedPipelineRuns(params));
};

const getRunningPipelineRuns = async (): Promise<FailedPipelineRunsResponse> => {
  const response = await axiosGet('/pipeline_runs/running', {}, 'v3');
  return response.data;
};

export const useRunningPipelineRuns = () => {
  return useQuery<FailedPipelineRunsResponse>(['running_pipeline_runs'], getRunningPipelineRuns, {
    refetchInterval: 5000,
  });
};

const markRunReviewed = async (runId: number, reviewed: boolean): Promise<FailedPipelineRun> => {
  const response = await axiosPut(`/pipeline_runs/${runId}/reviewed`, { reviewed }, 'v3');
  return response.data;
};

export const useMarkRunReviewed = () => {
  const queryClient = useQueryClient();

  return useMutation(({ runId, reviewed }: { runId: number; reviewed: boolean }) => markRunReviewed(runId, reviewed), {
    onSuccess: () => {
      toast.success('Pipeline run updated');
      queryClient.invalidateQueries(['failed_pipeline_runs']);
    },
    onError: () => {
      toast.error('Failed to update pipeline run');
    },
  });
};

const retryPipeline = async (runId: number): Promise<FailedPipelineRun> => {
  const response = await axiosPost(`/pipeline_runs/${runId}/retry`, {}, 'v3');
  return response.data;
};

export const useRetryPipeline = () => {
  const queryClient = useQueryClient();

  return useMutation((runId: number) => retryPipeline(runId), {
    onSuccess: () => {
      toast.success('Pipeline retry started');
      queryClient.invalidateQueries(['failed_pipeline_runs']);
      queryClient.invalidateQueries(['running_pipeline_runs']);
    },
    onError: () => {
      toast.error('Failed to retry pipeline');
    },
  });
};

const startPipelineForAudio = async (listenerAudioId: number): Promise<FailedPipelineRun> => {
  const response = await axiosPost('/pipeline_runs/start', { listener_audio_id: listenerAudioId }, 'v3');
  return response.data;
};

export const useStartPipeline = () => {
  const queryClient = useQueryClient();

  return useMutation((listenerAudioId: number) => startPipelineForAudio(listenerAudioId), {
    onSuccess: () => {
      toast.success('Pipeline started');
      queryClient.invalidateQueries(['failed_pipeline_runs']);
      queryClient.invalidateQueries(['running_pipeline_runs']);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || 'Failed to start pipeline';
      toast.error(message);
    },
  });
};
